import { Box, type Theme, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

type HeaderSubNavItem = {
	labelKey: string;
	path: string;
};

const HEADER_SUB_NAV: Record<string, HeaderSubNavItem[]> = {
	products: [
		{ labelKey: 'entities.products', path: '/products' },
		{ labelKey: 'entities.products/groups', path: '/products/groups' },
	],
	contracts: [
		{ labelKey: 'entities.contracts', path: '/contracts' },
		{ labelKey: 'entities.contracts/details', path: '/contracts/details' },
	],
	destPoint: [
		{ labelKey: 'entities.destPoint', path: '/destPoint' },
		{ labelKey: 'entities.destPoint/details', path: '/destPoint/details' },
	],
	warehouse: [
		{ labelKey: 'entities.warehouse', path: '/warehouse' },
		{
			labelKey: 'entities.inventorization',
			path: '/warehouse/inventorization',
		},
	],
};

const titleSx = {
	fontSize: { xs: 14, md: 16 },
	fontWeight: 500,
	color: (theme: Theme) => theme.palette.kemek.darkestGray,
};

// Portal target for page-specific controls rendered next to the title
// (e.g. the spy page's grid/cards toggle). Empty for pages that don't use it.
export const HEADER_TITLE_ACTIONS_ID = 'header-title-actions';

const TitleActionsSlot = () => (
	<Box
		id={HEADER_TITLE_ACTIONS_ID}
		sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
	/>
);

const HeaderSubNav = ({ headerTitle }: { headerTitle: string }) => {
	const { t } = useTranslation();
	const location = useLocation();

	const groupKey = location.pathname.split('/')[1];
	const items = HEADER_SUB_NAV[groupKey];

	if (!items) {
		return (
			<Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
				<Typography component="h1" sx={titleSx}>
					{headerTitle}
				</Typography>
				<TitleActionsSlot />
			</Box>
		);
	}

	const otherItems = items.filter((item) => location.pathname !== item.path);

	return (
		<Box
			component="nav"
			aria-label="page sub-navigation"
			sx={{
				display: 'flex',
				flexDirection: { xs: 'column', md: 'row' },
				alignItems: { xs: 'flex-start', md: 'center' },
				gap: { xs: '4px', md: '20px' },
			}}
		>
			<Typography component="h1" sx={titleSx} aria-current="page">
				{headerTitle}
			</Typography>

			<TitleActionsSlot />

			{otherItems.map((item) => (
				<Box
					key={item.path}
					sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}
				>
					<Link to={item.path} style={{ textDecoration: 'none' }}>
						<Box
							sx={{
								fontSize: { xs: 14, md: 16 },
								fontWeight: 500,
								color: (theme: Theme) => theme.palette.kemek.blue,
								'&:hover': {
									textDecoration: 'underline',
								},
							}}
						>
							{t(item.labelKey)}
						</Box>
					</Link>
				</Box>
			))}
		</Box>
	);
};

export default HeaderSubNav;
